import React, { useEffect, useState } from 'react';
import './DocumentsUploadPopup.scss';
import Rodal from '../../../components/Rodal/Rodal';
import { ModalBody, ModalHeader } from 'reactstrap';
import BootstrapTable from '../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import SortingOption, {
    SortOrder,
} from '../../../components/Table/models/bootstrap-table-sorting-option';
import { sortingFunction } from '../../../components/Table/models/table-helper';
import moment from 'moment';
import ButtonLoader from '../../../components/Layout/Buttons/ButtonLoader';
import FileUploadsList from '../../../components/FileUploadsList/FileUploadsList';
import { useStateSelector } from '../../../store/selectors';
import createNotification from '../../../utils/createNotification';
import AnswerFile from '../../../models/DataRequestHub/AnswerFile';
import UserDetailsModal from '../../Admin/Permissions/Users/Modal/UserDetailsModal';

interface DocumentsUploadPopupProps {
    header: string;
    uploadedFiles: AnswerFile[];
    close(): void;
    projectId: number;
    formId: number;
    questionId: number;
    onSuccessfulUpload(): void;
    isAdminView: boolean;
    allowedExtensions: string[];
    isReadonlyMode: boolean;
    bulkDownloadFileName?: string;
}
interface TableSortingState {
    data: any[];
    sortField: string;
    sortOrder: SortOrder;
}

const DocumentsUploadPopup = (props: DocumentsUploadPopupProps) => {
    const axios = useStateSelector((s) => s.core.axios);
    const [isLoading, setIsLoading] = useState(false);
    const [tableData, setTableData] = useState<AnswerFile[]>([]);
    const [selectedFiles, setSelectedFiles] = useState<File[]>();
    const [selectedFileIds, setSelectedFileIds] = useState<number[]>([]);
    const [isBulkDownloading, setIsBulkDownloading] = useState(false);
    const [isUserDetailsModalVisible, setIsUserDetailsModalVisible] =
        useState(false);
    const [selectedUserId, setSelectedUserId] = useState<number>();

    const defaultSorted: SortingOption = {
        dataField: 'dateCreated',
        order: 'desc',
    };
    const [sortingOption, setSortingOption] =
        useState<SortingOption>(defaultSorted);

    const isAllSelected =
        tableData.length > 0 && selectedFileIds.length === tableData.length;

    const isIndeterminate =
        selectedFileIds.length > 0 && selectedFileIds.length < tableData.length;

    const handleSelectAll = (isSelect: boolean) => {
        if (isSelect) {
            setSelectedFileIds(tableData.map((file) => file.id));
        } else {
            setSelectedFileIds([]);
        }
    };

    const handleOnSelect = (row: AnswerFile, isSelect: boolean) => {
        if (isSelect) {
            setSelectedFileIds((prev) =>
                prev.includes(row.id) ? prev : [...prev, row.id]
            );
        } else {
            setSelectedFileIds((prev) => prev.filter((id) => id !== row.id));
        }
    };

    const openUserDetails = (userId: number) => {
        setSelectedUserId(userId);
        setIsUserDetailsModalVisible(true);
    };

    const getColumns = () => [
        {
            dataField: 'id',
            text: '',
            hidden: true,
        },
        {
            dataField: 'name',
            text: 'File',
            sort: true,
            formatter: (cellContent: string, row: AnswerFile) => (
                <div className="link-text" onClick={() => openFile(row.id)}>
                    {cellContent}
                </div>
            ),
        },
        {
            dataField: 'dateCreated',
            text: 'Upload Date',
            sort: true,
            style: {
                width: '120px',
            },
            formatter: (cellContent: Date) => moment(cellContent).format('L'),
        },
        {
            dataField: 'creatorName',
            text: 'Creator',
            sort: true,
            formatter: (cellContent: string, row: AnswerFile) => {
                if (!cellContent) {
                    return '';
                }

                if (
                    props.isAdminView &&
                    row.creatorUserId &&
                    !row.creatorIsAdmin
                ) {
                    return (
                        <div
                            className="link-text"
                            onClick={() => openUserDetails(row.creatorUserId)}>
                            {cellContent}
                        </div>
                    );
                }

                return cellContent;
            },
        },
    ];

    const selectRow = {
        mode: 'checkbox' as const,
        clickToSelect: false,
        selected: selectedFileIds,
        onSelect: handleOnSelect,
        onSelectAll: handleSelectAll,
        selectionHeaderRenderer: ({ indeterminate, checked }: { indeterminate: boolean; checked: boolean }) => (
            <input
                type="checkbox"
                checked={checked || isAllSelected}
                ref={(input) => {
                    if (input) {
                        input.indeterminate = indeterminate || isIndeterminate;
                    }
                }}
                onChange={(e) => handleSelectAll(e.target.checked)}
            />
        ),
        selectionRenderer: ({ checked, rowIndex }: { checked: boolean; rowIndex: number }) => {
            const row = tableData[rowIndex];
            if (!row) return null;
            const isRowSelected = selectedFileIds.includes(row.id);
            return (
                <input
                    type="checkbox"
                    checked={isRowSelected}
                    onChange={() => handleOnSelect(row, !isRowSelected)}
                />
            );
        },
    };

    useEffect(() => {
        sortFunction(props.uploadedFiles ?? [], sortingOption);
    }, [props.uploadedFiles]);

    const handleTableChange = (_props: any, state: TableSortingState) => {
        const newSortingOption: SortingOption = {
            dataField: state.sortField,
            order: state.sortOrder as SortOrder,
        };
        setSortingOption(newSortingOption);
        sortFunction(state.data, newSortingOption);
    };

    const sortFunction = (data: any[], sortingOption: SortingOption) => {
        const sortedQuestions = sortingFunction([...data], sortingOption);
        setTableData(sortedQuestions);
    };

    const deleteFile = (file: File) => {
        if (!isLoading) {
            const index = selectedFiles.indexOf(file);
            if (index > -1) {
                selectedFiles.splice(index, 1);
                setSelectedFiles([...selectedFiles]);
            }
        }
    };

    const uploadFiles = async (files: File[]) => {
        const formFiles = files.map((f) => {
            const formData = new FormData();
            formData.append('File', f);
            return formData;
        });

        const requests = formFiles.map((f) =>
            axios.post(`/api/temporaryfiles`, f, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })
        );

        const responses = await Promise.all(requests);

        if (responses.some((r) => r.status != 200)) {
            createNotification(
                'Some files are not uploaded due to an unexpected error.',
                'error'
            );
            setIsLoading(false);
            throw new Error('An error occurent while file uploading');
        }

        return responses.map((m) => {
            return m.data as AnswerFile;
        });
    };

    const saveUploads = async () => {
        setIsLoading(true);
        const uploadedFiles = await uploadFiles(selectedFiles);
        const fileIds = uploadedFiles.map((m) => m.id);

        axios
            .post(
                `/api/dataRequestProject/${props.projectId}/dataRequestForm/${props.formId}/question/${props.questionId}/answer/answerFiles`,
                {
                    fileIds,
                }
            )
            .then((response) => {
                if (response.status === 200) {
                    const uploadedFilesCount = response.data;

                    if (uploadedFilesCount !== fileIds.length) {
                        createNotification(
                            `Some of files doesn't match allowed extensions. Count: ${
                                fileIds.length - uploadedFilesCount
                            }`,
                            'warning'
                        );
                    }

                    props.onSuccessfulUpload();
                    setSelectedFiles([]);
                } else {
                    createNotification('An error occured', 'error');
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const validateFilesSelection = (files: File[]) => {
        if (
            props.allowedExtensions.length === 0 ||
            props.allowedExtensions.some((s) => s.toLocaleLowerCase() === 'any')
        ) {
            return files;
        }

        const allowedFiles = files.filter((f) =>
            props.allowedExtensions.some((s) =>
                f.name.toLowerCase().endsWith(s)
            )
        );

        if (files.length > allowedFiles.length) {
            const notMathedFilesCount = files.length - allowedFiles.length;
            createNotification(
                `Some of files doesn't match allowed extensions. Count: ${notMathedFilesCount}`,
                'warning'
            );
        }

        return allowedFiles;
    };

    const handleFileSelection = (fileList: FileList) => {
        let files = Array.from(fileList).map((m) => m);
        files = validateFilesSelection(files);

        const newFiles = files.filter(
            (f) =>
                !selectedFiles?.some(
                    (s) =>
                        s.name.toLocaleLowerCase() ===
                        f.name.toLocaleLowerCase()
                )
        );

        setSelectedFiles(selectedFiles?.concat(newFiles) ?? newFiles);
    };

    const openFile = (fileId: number) => {
        axios
            .get(
                `/api/dataRequestProject/${props.projectId}/dataRequestForm/${props.formId}/question/${props.questionId}/answer/answerFiles/${fileId}`
            )
            .then((response) => {
                window.open(response.data, '_blank');
            });
    };

    const bulkDownloadFiles = async () => {
        if (selectedFileIds.length === 0) return;

        setIsBulkDownloading(true);
        try {
            const response = await axios.post(
                `/api/dataRequestProject/${props.projectId}/dataRequestForm/${props.formId}/question/${props.questionId}/answer/AnswerFiles/bulk-download`,
                { fileIds: selectedFileIds },
                { responseType: 'blob' }
            );

            if (response.status === 200) {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                const singleFile =
                    selectedFileIds.length === 1
                        ? tableData.find((f) => f.id === selectedFileIds[0])
                        : undefined;
                const fileName = singleFile
                    ? singleFile.name
                    : props.bulkDownloadFileName
                    ? `${props.bulkDownloadFileName}.zip`
                    : 'files.zip';
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                setSelectedFileIds([]);
            } else {
                createNotification(
                    'An error occurred while downloading files.',
                    'error'
                );
            }
        } catch {
            createNotification(
                'An error occurred while downloading files.',
                'error'
            );
        } finally {
            setIsBulkDownloading(false);
        }
    };

    const addMoreDocuments = () => document.getElementById('fileInput').click();

    return (
        <div className="documents-upload-popup">
            <Rodal
                visible={true}
                onClose={() => {
                    if (!isLoading) {
                        props.close();
                    }
                }}
                width={800}>
                <ModalHeader>{props.header}</ModalHeader>
                <ModalBody>
                    <div className="uploaded-documents-section">
                        <div className="section-header">
                            <label className="section-title">
                                Uploaded Documents
                            </label>
                            <ButtonLoader
                                buttonText="Download Selected"
                                loaderButtonText=""
                                disabled={
                                    selectedFileIds.length === 0 ||
                                    isBulkDownloading
                                }
                                isLoading={isBulkDownloading}
                                onClick={bulkDownloadFiles}
                                className="btn btn-primary btn-sm"
                            />
                        </div>
                        <div className="document-uploads-table">
                            <BootstrapTable
                                id="document-uploads-table"
                                striped
                                bootstrap4
                                remote
                                keyField="id"
                                data={tableData}
                                columns={getColumns()}
                                defaultSorted={[defaultSorted]}
                                onTableChange={handleTableChange}
                                selectRow={selectRow}
                                noDataIndication="No Data available"></BootstrapTable>
                        </div>
                    </div>
                    <div className="new-documents-section">
                        <label className="section-title">New Uploads</label>
                        <input
                            hidden
                            type="file"
                            id="fileInput"
                            onChange={(a) => {
                                handleFileSelection(a.target.files);
                                a.target.value = null;
                            }}
                            multiple
                            accept={props.allowedExtensions.join(',')}
                        />
                        <div className="uploads">
                            {selectedFiles ? (
                                <FileUploadsList
                                    fileList={selectedFiles}
                                    onDeleteClick={
                                        deleteFile
                                    }></FileUploadsList>
                            ) : (
                                []
                            )}
                        </div>
                        <button
                            className="btn btn-primary"
                            disabled={isLoading || props.isReadonlyMode}
                            onClick={addMoreDocuments}>
                            Add More Documents
                        </button>
                    </div>
                    <div className="buttons-wrapper ">
                        <button
                            disabled={isLoading}
                            className="btn btn-cancel"
                            onClick={props.close}>
                            {props.isAdminView ? 'Close' : 'Cancel'}
                        </button>
                        <ButtonLoader
                            buttonText={'Confirm & Upload'}
                            loaderButtonText={''}
                            disabled={
                                isLoading ||
                                selectedFiles?.length === 0 ||
                                !selectedFiles
                            }
                            isLoading={isLoading}
                            onClick={saveUploads}
                            className={'btn btn-primary'}
                        />
                    </div>
                </ModalBody>
            </Rodal>
            {isUserDetailsModalVisible && (
                <UserDetailsModal
                    isVisible={isUserDetailsModalVisible}
                    userID={selectedUserId}
                    onClose={() => setIsUserDetailsModalVisible(false)}
                />
            )}
        </div>
    );
};

export default DocumentsUploadPopup;
